// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline component for proposed event groups.
 * This module provides a visualization of planned but
 * unscheduled event groups for an asset. It filters
 * and displays event groups that have no dates set,
 * showing up to 5 upcoming proposed activities.
 *
 * Key features:
 * - Proposed event display
 * - Event group filtering
 * - Timeline visualization
 * - Empty state handling
 * - Limited display count
 */
import { Asset } from '@/asset/api/types';
import { AssetProposedEventGroupTimelineItem } from '@/asset/components/asset-proposed-event-group-timeline-item';
import { useEventGroups } from '@/asset/hooks/use-event-groups';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useState } from 'react';

/**
 * Props for configuring the AssetProposedEventGroupTimeline component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset whose proposed events are being displayed.
   * Used to fetch and filter relevant event groups.
   */
  asset: Asset;
}

/**
 * A timeline component for displaying proposed event groups.
 * Shows a list of planned but unscheduled event groups for
 * an asset, limited to the 5 most recent proposals. Includes
 * an empty state message when no proposed events exist.
 *
 * Features:
 * - Proposed event display
 * - Event group filtering
 * - Timeline visualization
 * - Empty state handling
 * - Limited display count
 *
 * Usage example:
 * ```tsx
 * function AssetTimeline({ asset }: Props) {
 *   return (
 *     <Box>
 *       <Typography variant="h6">Proposed Events</Typography>
 *       <AssetProposedEventGroupTimeline asset={asset} />
 *     </Box>
 *   );
 * }
 * ```
 */
export function AssetProposedEventGroupTimeline(props: Props) {
  // Fetch event groups for the asset
  const { data } = useEventGroups(props.asset.key, {});
  // Pagination state
  const [count, setCount] = useState(5);
  // Filter for proposed event groups (no occurred at dates set)
  // and limit to 5 most recent
  const proposedEventGroups =
    data?.eventGroups
      // Remove anything that has occurred
      .filter((eventGroup) => {
        return eventGroup.events.every((event) => {
          return event.occurredAt === null;
        });
      }) ?? [];
  const sortedProposedEventGroups = proposedEventGroups.sort((a, b) => {
    // Find the earliest expectedAt date for each event group, only considering Ship events
    const aExpectedAt = Math.min(
      ...a.events
        .filter((event) => event.type.name === 'Ship')
        .map((event) =>
          event.expectedAt ? event.expectedAt.toMillis() : Infinity
        )
    );
    const bExpectedAt = Math.min(
      ...b.events
        .filter((event) => event.type.name === 'Ship')
        .map((event) =>
          event.expectedAt ? event.expectedAt.toMillis() : Infinity
        )
    );
    // If both are Infinity (all null dates), treat them as equal
    if (aExpectedAt === Infinity && bExpectedAt === Infinity) return 0;
    // If one is Infinity, put it last
    if (aExpectedAt === Infinity) return 1;
    if (bExpectedAt === Infinity) return -1;
    // Sort in ascending order (newest first)
    return bExpectedAt - aExpectedAt;
  });

  const hasMoreData = count < sortedProposedEventGroups.length;
  const pagedEventGroups = sortedProposedEventGroups.slice(0, count) ?? [];
  const handleClickMore = () => {
    setCount((currentCount) => currentCount + 5);
  };
  // Show empty state message if no proposed events
  if (pagedEventGroups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        There is no planned activity
      </Typography>
    );
  }

  // Render timeline of proposed event groups
  return (
    <Stack>
      {pagedEventGroups.map((eventGroup, index) => (
        <Box key={eventGroup.id}>
          <Box display="grid" gridTemplateColumns="auto 1fr auto" gap={2}>
            <AssetProposedEventGroupTimelineItem
              asset={props.asset}
              eventGroup={eventGroup}
            />
          </Box>
          {index < pagedEventGroups.length - 1 && <Divider sx={{ my: 2 }} />}
        </Box>
      ))}
      {hasMoreData && (
        <Button
          size="small"
          sx={{ alignSelf: 'start' }}
          onClick={handleClickMore}
        >
          See more
        </Button>
      )}
    </Stack>
  );
}
